import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
    Table,
    Button,
    Flex,
    Breadcrumb,
    Form,
    message,
    Popconfirm,
    Space,
    Pagination,
    Switch,
    TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import GatePassModal from './GatePassModel'; // This is the modal component
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
    customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface GatePass {
    gatepass_id: number;
    gatepass_receiver_name: string;
    gatepass_status: number;
    gatepass_relation?: string;
    student_name?: string;
    gatepass_mobile?: string;
    approved_by?: string;
    gatepass_detail?: string;
    created_on?: string;
    gatepass_student_id?: number;
    gatepass_approve_by?: number;
}

const GatePass: React.FC = () => {
    const [gatepassData, setGatePass] = useState<GatePass[]>([]);
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [initialValues, setInitialValues] = useState<GatePass | null>(null);
    const { styles } = useStyle();
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const currentModule = useCurrentModule();

    const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
    const [sortedInfo, setSortedInfo] = useState<any>({});

    const cancel: PopconfirmProps['onCancel'] = (e) => {
        console.log(e);
        showError('Click on No');
    };

    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    const fetchGatePass = async () => {
        try {
            const data = await apiRequest<GatePass[]>({
                method: 'get',
                url: 'gate-pass',
            });
            setGatePass(data);
        } catch (error) {
            showError('Failed to fetch gate-pass');
            console.error(error);
        }
    };

    useEffect(() => {
        fetchGatePass();
    }, []);

    const handleEditClick = (record: GatePass) => {
        setIsEditMode(true);
        setInitialValues({
            gatepass_id: record.gatepass_id,
            gatepass_receiver_name: record.gatepass_receiver_name,
            gatepass_mobile: record.gatepass_mobile,
            gatepass_relation: record.gatepass_relation,
            gatepass_detail: record.gatepass_detail,
            gatepass_status: record.gatepass_status,
            gatepass_student_id: record.gatepass_student_id,
            gatepass_approve_by: record.gatepass_approve_by,
        });
        setIsModalVisible(true);
    };

    const handleCreate = () => {
        fetchGatePass();
    };

    const showCreateModal = () => {
        setIsEditMode(false);
        setInitialValues(null);
        setIsModalVisible(true);
    };

    const hideCreateModal = () => {
        setIsModalVisible(false);
        setIsEditMode(false);
        setInitialValues(null);
    };

    const handleDelete = async (gatepass_id: number) => {
        try {
            await apiRequest({
                method: 'delete',
                url: `/delete-gatepass/${gatepass_id}`,
            });
            showSuccess('Gate Pass deleted');
            fetchGatePass();
        } catch {
            showError('Failed to delete gate-pass');
        }
    };

    const handleStatusToggle = async (gatepass_id: number) => {
        setGatePass((prev) =>
            prev.map((item) =>
                item.gatepass_id === gatepass_id
                    ? { ...item, gatepass_status: item.gatepass_status === 1 ? 0 : 1 }
                    : item
            )
        );

        try {
            await apiRequest({
                method: 'put',
                url: `/update-status-gatepass/${gatepass_id}`,
            });
            showSuccess('Gate Pass status updated');
        } catch (error) {
            showError('Failed to update status');
            setGatePass((prev) =>
                prev.map((item) =>
                    item.gatepass_id === gatepass_id
                        ? { ...item, gatepass_status: item.gatepass_status === 1 ? 0 : 1 }
                        : item
                )
            );
        }
    };

    const handleTableChange: TableProps<GatePass>['onChange'] = (pagination, filters, sorter) => {
        setFilteredInfo(filters);
        setSortedInfo(sorter);
    };

    const columns: TableColumnsType<GatePass> = [
        {
            title: 'Student Info',
            key: 'student_info',
            filters: Array.from(
                new Set(
                    gatepassData
                        .map(s => `${s.student_name || ''} | ${s.gatepass_mobile || ''} | ${s.gatepass_relation || ''}`)
                        .filter(Boolean)
                )
            ).map(info => ({
                text: info,
                value: info,
            })),
            filterSearch: true,
            filteredValue: filteredInfo.student_info || null,
            onFilter: (value, record) => {
                const combined = `${record.student_name || ''} | ${record.gatepass_mobile || ''} | ${record.gatepass_relation || ''}`;
                return combined.includes(value as string);
            },
            sorter: (a, b) => {
                const aValue = `${a.student_name || ''} ${a.gatepass_mobile || ''} ${a.gatepass_relation || ''}`.toLowerCase();
                const bValue = `${b.student_name || ''} ${b.gatepass_mobile || ''} ${b.gatepass_relation || ''}`.toLowerCase();
                return aValue.localeCompare(bValue);
            },
            sortOrder: sortedInfo.columnKey === 'student_info' ? sortedInfo.order : null,
            ellipsis: true,
            render: (_, record) => (
                <div>
                    <div><strong>{record.student_name || '-'}</strong></div>
                    <div>{record.gatepass_mobile || '-'}</div>
                    <div>{record.gatepass_relation || '-'}</div>
                </div>
            ),
        },
        {
            title: 'Detail',
            dataIndex: 'gatepass_detail',
            key: 'gatepass_detail',
            ellipsis: true,
            render: (_, record) => <div>{record.gatepass_detail || '-'}</div>,
        },
        {
            title: 'Approved By',
            dataIndex: 'approved_by',
            key: 'approved_by',
            filters: Array.from(new Set(gatepassData.map((s) => s.approved_by).filter(Boolean))).map((name) => ({
                text: name!,
                value: name!,
            })),
            filterSearch: true,
            filteredValue: filteredInfo.approved_by || null,
            onFilter: (value, record) => (record.approved_by || '').includes(value as string),
            sorter: (a, b) => (a.approved_by || '').localeCompare(b.approved_by || ''),
            sortOrder: sortedInfo.columnKey === 'approved_by' ? sortedInfo.order : null,
            ellipsis: true,
            render: (_, record) => <div>{record.approved_by || '-'}</div>,
        },
        {
            title: 'Created On',
            dataIndex: 'created_on',
            key: 'created_on',
        }
    ];

    if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
        columns.push({
            title: 'Action',
            width: 200,
            render: (_: any, record: GatePass) => (
                <Space size="middle">
                    {currentModule?.right_edit === 'Y' && (
                        <>
                            <Switch
                                checkedChildren="Active"
                                unCheckedChildren="Inactive"
                                checked={!!record.gatepass_status}
                                onChange={() => handleStatusToggle(record.gatepass_id)}
                            />
                            <Button onClick={() => handleEditClick(record)} className="px-8">
                                <EditOutlined />
                            </Button>
                        </>
                    )}
                    {currentModule?.right_delete === 'Y' && (
                        <Popconfirm
                            title="Delete this gate-pass?"
                            onConfirm={() => handleDelete(record.gatepass_id)}
                            onCancel={cancel}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button danger className="px-8">
                                <DeleteOutlined />
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        });
    }

    return (
        <>
            <Space
                style={{
                    display: 'flex',
                    marginBottom: 16,
                    alignItems: 'center',
                    width: '100%',
                    justifyContent: 'space-between',
                }}
            >
                <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
                    <Breadcrumb.Item href="/">
                        <HomeOutlined />
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>View Gate Pass</Breadcrumb.Item>
                </Breadcrumb>

                {currentModule?.right_add === 'Y' && (
                    <Flex gap="small">
                        <Button type="primary" onClick={showCreateModal}>
                            Add Gate Pass
                        </Button>
                    </Flex>
                )}
            </Space>

            <div
                style={{
                    minHeight: 360,
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                }}
            >
                <Table<GatePass>
                    tableLayout="auto"
                    className={`${styles.customTable} scrollable-table`}
                    columns={columns}
                    onChange={handleTableChange}
                    rowKey="gatepass_id"
                    dataSource={gatepassData.slice(
                        (pagination.current - 1) * pagination.pageSize,
                        pagination.current * pagination.pageSize
                    )}
                    bordered
                    pagination={false}
                    scroll={{ y: 37 * 11 }}
                    locale={{ emptyText: 'No Gate Pass Found.' }}
                    footer={() => (
                        <Flex justify="space-between">
                            <span>
                                Showing {(pagination.current - 1) * pagination.pageSize + 1}–{Math.min(pagination.current * pagination.pageSize, gatepassData.length)} of {gatepassData.length}
                            </span>
                            <Pagination
                                {...pagination}
                                total={gatepassData.length}
                                showSizeChanger
                                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
                            />
                        </Flex>
                    )}
                />
            </div>

            <GatePassModal
                visible={isModalVisible}
                onCancel={hideCreateModal}
                onCreate={handleCreate}
                isEditMode={isEditMode}
                initialValues={initialValues}
            />
        </>
    );
};

export default GatePass;
